// This checks URL syntax/schemes only. The preview/save service owns provider
// recognition and content policy; unfamiliar websites remain ordinary links.
export const LINK_LABEL_ATTRIBUTE = 'data-wiw-link-label';
export const LINK_LABEL_MAX_WORDS = 3;
export const LINK_LABEL_MAX_CHARS = 24;

export function measureLinkLabel(value) {
  const text = String(value ?? '').normalize('NFC').replace(/\s+/gu, ' ').trim();
  const length = Array.from(text).length;
  const words = text ? text.split(' ').length : 0;
  return { text, length, words, valid: length > 0 && length <= LINK_LABEL_MAX_CHARS
    && words <= LINK_LABEL_MAX_WORDS && !Array.from(text).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) };
}

export function validLinkLabel(value) {
  const result = measureLinkLabel(value);
  return result.valid ? result.text : null;
}

export function isInstagramUrl(value) {
  try { return /(^|\.)instagram\.com$/i.test(new URL(value).hostname); }
  catch { return false; }
}

export function compactLinkLabel(href, label) {
  return isInstagramUrl(href) ? Array.from(String(label || '').replace(/\s+/gu, ' ').trim()).slice(0, 120).join('') : label;
}

// Official Instagram clipboard embeds include the whole caption, inline styles
// and provider chrome. Keep one bounded link; leave ordinary quoted prose alone.
export function compactPastedExternalHTML(value) {
  const html = String(value ?? '');
  if (!/instagram-media|data-instgrm-permalink/i.test(html)) return html;
  // Template contents are inert: discarded provider images/iframes must not
  // fetch or run merely because clipboard markup is being inspected.
  const container = document.createElement('template');
  container.innerHTML = html;
  container.content.querySelectorAll('blockquote.instagram-media[data-instgrm-permalink]').forEach(block => {
    const href = normalizeExternalUrl(block.getAttribute('data-instgrm-permalink'));
    if (!href || !isInstagramUrl(href)) return;
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.textContent = compactLinkLabel(href, block.textContent || href);
    block.replaceWith(anchor);
  });
  return container.innerHTML;
}

export function normalizeExternalUrl(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || /\s/.test(text) || Array.from(text).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  try {
    const url = new URL(/^www\./i.test(text) ? `https://${text}` : text);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

function trimPunctuation(value) {
  let end = value.length;
  const punctuation = char => '.,!?;:'.includes(char);
  while (end && punctuation(value[end - 1])) end--;
  const pairs = { ')': '(', ']': '[', '}': '{' };
  const counts = { '(': 0, ')': 0, '[': 0, ']': 0, '{': 0, '}': 0 };
  for (let index = 0; index < end; index++) {
    if (Object.prototype.hasOwnProperty.call(counts, value[index])) counts[value[index]]++;
  }
  // A long pasted tail of closing brackets must remain prose without doing
  // one full-string split/allocation for every character on the UI thread.
  while (end && pairs[value[end - 1]]) {
    const close = value[end - 1];
    if (counts[close] <= counts[pairs[close]]) break;
    counts[close]--;
    end--;
    while (end && punctuation(value[end - 1])) end--;
  }
  return value.slice(0, end);
}

export function externalLinkSegments(value) {
  const text = String(value ?? '');
  const pattern = /\[([^\]\n]{1,120})\]\((?=(?:https?:\/\/|www\.))|(?:https?:\/\/|www\.)[^\s<>"'`«»“”]+/gi;
  const segments = [];
  let end = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index < end) continue;
    // Avoid extracting a link out of an email or a larger word/token.
    if (match.index && /[\p{L}\p{N}_@/]/u.test(text[match.index - 1])) continue;
    if (match[1]) {
      const customLabel = validLinkLabel(match[1]);
      const start = match.index + match[0].length;
      let depth = 1, cursor = start;
      for (; cursor < text.length && !/[\s<>"'`]/u.test(text[cursor]); cursor++) {
        if (text[cursor] === '(') depth++;
        if (text[cursor] === ')' && --depth === 0) break;
      }
      const href = depth === 0 && normalizeExternalUrl(text.slice(start, cursor));
      if (href && customLabel) {
        if (match.index > end) segments.push({ text: text.slice(end, match.index) });
        end = cursor + 1;
        segments.push({ text: text.slice(match.index, end), href, customLabel });
      }
      continue;
    }
    const label = trimPunctuation(match[0]);
    const href = normalizeExternalUrl(label);
    if (!href) continue;
    if (match.index > end) segments.push({ text: text.slice(end, match.index) });
    segments.push({ text: label, href });
    end = match.index + label.length;
  }
  if (end < text.length || !segments.length) segments.push({ text: text.slice(end) });
  return segments;
}

// Upgrade old saved/draft plain links before Tiptap parses the document. This
// runs only at restoration boundaries, never on live input/IME transactions.
export function linkifyExternalHTML(value) {
  const html = String(value ?? '');
  if (!/(?:https?:\/\/|www\.)/i.test(html)) return html;
  const container = document.createElement('template');
  container.innerHTML = html;
  const walker = document.createTreeWalker(container.content, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  let changed = false;
  texts.forEach(node => {
    if (node.parentElement?.closest('a,pre,code,script,style,textarea')) return;
    const segments = externalLinkSegments(node.textContent);
    if (!segments.some(segment => segment.href)) return;
    const fragment = document.createDocumentFragment();
    segments.forEach(segment => {
      if (!segment.href) fragment.appendChild(document.createTextNode(segment.text));
      else {
        const anchor = document.createElement('a');
        anchor.setAttribute('href', segment.href);
        if (segment.customLabel) anchor.setAttribute(LINK_LABEL_ATTRIBUTE, segment.customLabel);
        anchor.textContent = segment.customLabel || compactLinkLabel(segment.href, segment.text);
        fragment.appendChild(anchor);
      }
    });
    node.replaceWith(fragment);
    changed = true;
  });
  return changed ? container.innerHTML : html;
}
